import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldWarning, MagnifyingGlass, ArrowsClockwise } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { StandardReports } from "../components/StandardReports";

const TABS = ["Cases", "Screen a Name", "Watchlist Status", "Reports"];

export default function AMLPage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "Cases");
  return (
    <div className="p-6 sm:p-8" data-testid="aml-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / AML · WATCHLIST SCREENING</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-1 flex items-center gap-3">
          AML Watchlist.
          <span className="text-xs font-mono px-2 py-1 bg-[#FF3E00] text-white uppercase tracking-widest">OFAC SDN</span>
        </h1>
        <div className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Every new vendor + customer + invoice party is auto-screened against the US Treasury OFAC SDN list. Hits land here as cases for review.
        </div>
      </div>
      <div className="flex border-b border-zinc-950 mb-0 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`aml-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors whitespace-nowrap ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="border-x border-b border-zinc-950 min-h-[60vh]">
        {tab === "Cases" && <CasesPanel />}
        {tab === "Screen a Name" && <ScreenPanel />}
        {tab === "Watchlist Status" && <WatchlistStatusPanel />}
        {tab === "Reports" && <StandardReports module="aml" />}
      </div>
    </div>
  );
}

function CasesPanel() {
  const [status, setStatus] = useState("open");
  const [cases, setCases] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const r = await api.get(`/aml/cases?status=${status}`);
    setCases(r.data);
  };
  useEffect(() => { load().catch(() => {}); }, [status]);   

  const decide = async (id, decision) => {
    setBusy(id);
    try {
      await api.post(`/aml/cases/${id}/decide`, { decision, comment: "" });
      toast.success(`Marked ${decision}`);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-6" data-testid="cases-panel">
      <div className="mb-4 flex items-center gap-2">
        {["open", "cleared", "escalated", "closed"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`text-[10px] font-mono px-3 py-1 border ${status === s ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300"}`} data-testid={`case-filter-${s}`}>
            {s.toUpperCase()}
          </button>
        ))}
      </div>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs uppercase">
            <th className="text-left px-3 py-2">Subject</th>
            <th className="text-left px-3 py-2">Kind</th>
            <th className="text-left px-3 py-2">Top Match</th>
            <th className="text-left px-3 py-2">Program</th>
            <th className="text-right px-3 py-2">Score</th>
            <th className="text-left px-3 py-2">Created</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody data-testid="cases-tbody">
          {cases.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-zinc-400 text-sm">No cases in this status.</td></tr>}
          {cases.map((c) => {
            const top = c.hits?.[0] || {};
            return (
              <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`case-row-${c.id}`}>
                <td className="px-3 py-2 font-semibold">{c.subject_name}</td>
                <td className="px-3 py-2 text-xs"><span className="font-mono px-1.5 py-0.5 border border-zinc-300">{c.subject_kind}</span></td>
                <td className="px-3 py-2">{top.name} <span className="text-[10px] font-mono text-zinc-400">#{top.ent_num}</span></td>
                <td className="px-3 py-2 text-xs">{top.program}</td>
                <td className="px-3 py-2 text-right tnum text-[#FF3E00] font-semibold">{top.score}%</td>
                <td className="px-3 py-2 text-xs text-zinc-500">{c.created_at?.slice(0, 10)}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  {c.status === "open" && (
                    <>
                      <button onClick={() => decide(c.id, "cleared")} disabled={busy === c.id} className="text-[10px] font-mono px-2 py-1 border border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white" data-testid={`case-clear-${c.id}`}>CLEAR</button>
                      <button onClick={() => decide(c.id, "escalated")} disabled={busy === c.id} className="text-[10px] font-mono px-2 py-1 bg-[#FF3E00] text-white" data-testid={`case-escalate-${c.id}`}>ESCALATE</button>
                      <button onClick={() => decide(c.id, "closed")} disabled={busy === c.id} className="text-[10px] font-mono px-2 py-1 border border-zinc-950" data-testid={`case-close-${c.id}`}>CLOSE</button>
                    </>
                  )}
                  {c.status !== "open" && <span className="text-[10px] font-mono text-zinc-400">{c.status.toUpperCase()}</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ScreenPanel() {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/aml/screen", { name, subject_kind: "adhoc" });
      setResult(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="p-6 max-w-2xl" data-testid="screen-panel">
      <form onSubmit={submit} className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Name to screen (person or entity)" className="flex-1 border-b-2 border-zinc-950 bg-transparent py-2 text-sm outline-none" data-testid="screen-name-input" />
        <button type="submit" disabled={busy || !name} className="bg-zinc-950 text-white text-xs font-semibold px-5 py-2 hover:bg-[#FF3E00] disabled:opacity-30 flex items-center gap-1" data-testid="screen-submit">
          <MagnifyingGlass size={14} /> {busy ? "SCREENING…" : "SCREEN"}
        </button>
      </form>
      {result && (
        <div className="mt-6" data-testid="screen-result">
          {result.matched ? (
            <div className="border-2 border-[#D0021B] p-4">
              <div className="font-mono text-[10px] tracking-widest text-[#D0021B]">⚠ POTENTIAL MATCH — CASE OPENED</div>
              <table className="w-full text-sm mt-3">
                <thead>
                  <tr className="text-xs uppercase text-zinc-500">
                    <th className="text-left py-1">Name</th>
                    <th className="text-left py-1">Type</th>
                    <th className="text-left py-1">Program</th>
                    <th className="text-right py-1">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {result.hits.map((h) => (
                    <tr key={h.ent_num} className="border-t border-zinc-100">
                      <td className="py-1.5">{h.name}</td>
                      <td className="py-1.5 text-xs">{h.type}</td>
                      <td className="py-1.5 text-xs">{h.program}</td>
                      <td className="py-1.5 text-right tnum text-[#FF3E00] font-semibold">{h.score}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="border-2 border-emerald-600 p-4">
              <div className="font-mono text-[10px] tracking-widest text-emerald-700">✓ NO MATCH</div>
              <div className="mt-2 text-sm">No entries on the OFAC SDN list match this name above the {result.note ? "threshold." : "82% threshold."}</div>
              {result.note && <div className="text-xs text-amber-700 mt-2">{result.note}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WatchlistStatusPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/aml/watchlist/status").then((r) => setStatus(r.data));
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/aml/watchlist/refresh");
      if (r.data.ok) {
        toast.success(`Loaded ${r.data.rows} OFAC SDN entries`);
        await load();
      } else {
        toast.error(r.data.reason || "Refresh failed");
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6" data-testid="watchlist-status-panel">
      <div className="border border-zinc-950 max-w-lg">
        <div className="bg-zinc-950 text-white px-5 py-3 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-400">SOURCE</div>
            <div className="font-display text-lg font-black">OFAC SDN List</div>
          </div>
          <ShieldWarning size={28} weight="duotone" className="text-[#FF3E00]" />
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="label-sm text-zinc-500">Entries loaded</div>
            <div className="font-display font-black text-4xl tnum">{status?.count?.toLocaleString() || "—"}</div>
          </div>
          <div>
            <div className="label-sm text-zinc-500">Last refreshed</div>
            <div className="tnum text-sm">{status?.last_loaded ? new Date(status.last_loaded).toLocaleString() : "never — click refresh"}</div>
          </div>
          <button onClick={refresh} disabled={busy} className="w-full bg-zinc-950 text-white text-xs font-semibold py-3 hover:bg-[#FF3E00] flex items-center justify-center gap-2 disabled:opacity-30" data-testid="watchlist-refresh-btn">
            <ArrowsClockwise size={14} /> {busy ? "DOWNLOADING…" : "REFRESH FROM US TREASURY"}
          </button>
          <div className="text-xs text-zinc-500">
            Nightly re-screen runs at 04:00 UTC. The full OFAC SDN CSV is downloaded from <span className="font-mono">www.treasury.gov/ofac/downloads/sdn.csv</span> and re-matched against every vendor and customer.
          </div>
        </div>
      </div>
    </div>
  );
}
